import type { EffectiveRole } from '@/lib/types';
import { START_ORDER } from './content';

/**
 * الجولةُ الإرشادية (27 سبتمبر 2026) — خطواتُها بحسب الدور، وكلُّ خطوةٍ تشير إلى عنصرٍ يحمل
 * `data-tour`. الهدفُ `nav:<المسار>` بندُ القائمة (أو تبويبُ الشريط السفلي على الجوال — القيمةُ نفسُها
 * على الاثنين، والجولةُ تأخذ أولَ ظاهرٍ منهما)؛ وما لا يظهر (قائمةٌ مطويّة على الجوال) تُعرض خطوتُه
 * في الوسط بلا تظليل — فلا تتعطّل الجولةُ بعرض الشاشة.
 *
 * `TOUR_VERSION`: رفعُه يعيد الجولةَ **مرّةً واحدة** لكل من رآها (الخادمُ يحفظ آخرَ إصدارٍ رآه).
 * ارفعه حين تتغيّر الواجهةُ تغيّراً يستحقّ الشرح، لا عند كل تعديل نصّ. وبذرُ العرض يعلّم حساباته
 * بهذا الإصدار (`api/scripts/demo-seed.mjs`) — حدّثه معه.
 */
export const TOUR_VERSION = 1;

export interface TourStep {
  /** قيمةُ `data-tour` للعنصر المشار إليه — بلا هدفٍ تُعرض الخطوةُ في الوسط */
  target?: string;
  title: string;
  text: string;
}

/** العناصرُ الثابتة في القشرة التي تشير إليها الجولة — `test/help.test.ts` يتحقّق أنها موجودة */
export const SHELL_TARGETS = ['context', 'nav', 'quick', 'help', 'account'] as const;

type RoleSteps = Array<{ to: string; title: string; text: string }>;

/** بابان أو ثلاثة لكل دور — من قائمته هو، و`test/help.test.ts` يسقط إن لم يرها صاحبُ الدور */
const ROLE_STEPS: Partial<Record<EffectiveRole, RoleSteps>> = {
  PLATFORM_ADMIN: [
    { to: '/platform/accounts', title: 'الحسابات', text: 'كل الحسابات في مكانٍ واحد: ابحث وصفِّ وافتح ملفّ الحساب الكامل.' },
    { to: '/platform/admins', title: 'حسابات الإدارة', text: 'أنشئ حسابات الأقسام هنا واختر لكلٍّ قسمه.' },
    { to: '/platform/health', title: 'صحة التشغيل', text: 'الخادمان بحملهما، والتنبيهات بنصائحها، ومتى يُطوَّر الخادم، والنسخ الاحتياطي — راجعها كل يوم.' },
  ],
  PLATFORM_SUPPORT: [
    { to: '/platform/contact-messages', title: 'رسائل التواصل', text: 'ما يصل من نموذج «تواصل معنا» — عالجه واكتب ما فعلت.' },
    { to: '/platform/accounts', title: 'الحسابات', text: 'ابحث عن صاحب المشكلة بالاسم أو الجوال أو الرقم المرجعي.' },
    { to: '/platform/notes', title: 'ملاحظات التحسين', text: 'اكتب ملاحظةً لجهةٍ وتابع ردّها.' },
  ],
  PLATFORM_ACADEMIES: [
    { to: '/platform/academies', title: 'الأكاديميات', text: 'راجع الأكاديميات واعتمدها.' },
    { to: '/platform/content-reports', title: 'بلاغات المحتوى', text: 'احسم البلاغات: إخفاءٌ يُبلغ صاحبه أو إبقاء.' },
    { to: '/platform/notes', title: 'ملاحظات التحسين', text: 'اكتب ملاحظةً لأكاديمية أو مقدّم دورات وتابع ردّه.' },
  ],
  PLATFORM_INSTITUTIONS: [
    { to: '/platform/institution-requests', title: 'طلبات التسجيل', text: 'طلبات المراكز والمدارس الجديدة — اقبلها أو ارفضها بسبب.' },
    { to: '/platform/institutions', title: 'المؤسسات', text: 'اكشف على أي مؤسسة: أعدادها وكادرها وآخر نشاط.' },
    { to: '/platform/notes', title: 'ملاحظات التحسين', text: 'اكتب ملاحظةً لمؤسسة وتابع ردّ إدارتها.' },
  ],
  PLATFORM_FINANCE: [
    { to: '/platform/payouts', title: 'طلبات السحب', text: 'اصرف طلبات السحب المعلّقة أو ارفضها — الرفض يعيد المبلغ.' },
    { to: '/platform/pricing', title: 'التسعير', text: 'رسوم المنصة الساريّة — كل تغييرٍ يسري على ما يأتي لا على ما مضى.' },
  ],
  REGULATORY_SUPERVISOR: [
    { to: '/oversight', title: 'المؤسسات المرتبطة', text: 'افتح أي مؤسسةٍ مرتبطة بجهتك واقرأ سجلّاتها — القراءة وحدها.' },
  ],
  INSTITUTION_ADMIN: [
    { to: '/app/link-requests', title: 'طلبات الربط', text: 'الطلبة الجدد يصلون من هنا — وليّ الأمر يطلب وأنت تقبل.' },
    { to: '/app/users', title: 'المستخدمون', text: 'أنشئ حسابات الكادر وحدّد لكلٍّ دوره.' },
    { to: '/app/review', title: 'مركز المراجعة', text: 'الخطط والتقارير التي تنتظر اعتمادك، مرتّبةً بمهلتها.' },
  ],
  INTERNAL_SUPERVISOR: [
    { to: '/app/review', title: 'مركز المراجعة', text: 'ابدأ بما تجاوز مهلته، ثم اعتمد أو أعد بملاحظة.' },
    { to: '/app/plans', title: 'الخطط', text: 'كل خطط المؤسسة وأهدافها وحالاتها.' },
  ],
  SPECIALIST: [
    { to: '/app/caseload', title: 'الطلبة المسندون إليك', text: 'من هنا تفتح ملفّ كل طالبٍ أُسند إليك.' },
    { to: '/app/preparations', title: 'التحضير', text: 'حضّر الجلسة: أهدافٌ من الخطة ولكلٍّ مدّته واستراتيجيته.' },
    { to: '/app/sessions', title: 'الجلسات', text: 'ابدأ الجلسة في موعدها وسجّل المحاولات فيها.' },
  ],
  COUNSELOR: [
    { to: '/app/caseload', title: 'الطلبة المسندون إليك', text: 'من هنا تفتح ملفّ كل طالبٍ أُسند إليك.' },
    { to: '/app/counseling', title: 'الإرشاد', text: 'خطة الإرشاد وجلساتها وملاحظاتك الخاصة.' },
  ],
  TEACHER: [
    { to: '/me/class', title: 'صفّي', text: 'طلبتك في مكانٍ واحد، ومنه تفتح صفحة كل طالب.' },
    { to: '/app/attendance', title: 'الحضور', text: 'سجّل الحضور كل صباح — الغياب يصل وليّ الأمر تنبيهاً.' },
    { to: '/me/assignments', title: 'الواجبات', text: 'واجبات طلبتك وما سُلّم منها.' },
  ],
  PARENT: [
    { to: '/me/children', title: 'أبنائي', text: 'أضف طفلك واطلب ربطه بمؤسسة، ومنه ملفّه وتقاريره وتمارينه.' },
    { to: '/me/consents', title: 'الموافقات', text: 'ما وقّعته من موافقات — وسحبُ أيٍّ منها يسري فوراً.' },
    { to: '/me/reports', title: 'التقارير', text: 'التقارير التي نُشرت لك عن طفلك.' },
  ],
  PROFESSIONAL: [
    { to: '/me/professional', title: 'ملفّي المهني', text: 'أكمل مؤهلك ونبذتك وخبراتك، واختر إن كان ملفّك يظهر علناً.' },
    { to: '/me/invitations', title: 'الدعوات', text: 'دعوات المؤسسات إليك — لا يرى أحدٌ بريدك قبل أن تقبل.' },
  ],
  CLIENT: [
    { to: '/services', title: 'الخدمات', text: 'تصفّح الخدمات الاستشارية واحجز من الأوقات المتاحة.' },
    { to: '/me/consultations', title: 'استشاراتي', text: 'مواعيدك وما انعقد منها.' },
  ],
};

/** الدورُ الذي تُبنى له الجولة: أولُ ما يملكه صاحبُ الحساب بترتيب «ابدأ من هنا» */
export function tourRole(roles: readonly EffectiveRole[]): EffectiveRole | null {
  return (START_ORDER as EffectiveRole[]).find((r) => roles.includes(r)) ?? null;
}

/** خطواتُ الجولة لهذه الصفات — بين ست وتسع */
export function tourFor(roles: readonly EffectiveRole[]): TourStep[] {
  const role = tourRole(roles);
  const own = (role && ROLE_STEPS[role]) || [];
  return [
    { title: 'أهلاً بك في رفرفة', text: 'جولةٌ قصيرة تعرّفك بمكان كل شيء. تستطيع تخطّيها الآن وإعادتها متى شئت من زرّ المساعدة.' },
    { target: 'context', title: 'أين أنت؟', text: 'هنا المؤسسة التي تعمل فيها الآن ودورك فيها — كل ما تراه على الشاشة مشتقٌّ منهما.' },
    { target: 'nav', title: 'القائمة', text: 'كل ما يخصّك من أبواب. على الجوال تجدها في «المزيد» أسفل الشاشة.' },
    ...own.map((s) => ({ target: `nav:${s.to}`, title: s.title, text: s.text })),
    { target: 'quick', title: 'اختصاراتك', text: 'أزرارٌ لأكثر ما تفعله. خصّصها ورتّبها من زرّ «خصّص» — تتبعك على كل أجهزتك.' },
    { target: 'help', title: 'المساعدة', text: 'من هنا تعيد هذه الجولة، وتفتح مركز المساعدة، وتراسلنا. واضغط «?» في أي صفحة لتفتح المساعدة.' },
    { target: 'account', title: 'حسابك', text: 'اسمك وجوالك وكلمة المرور والتحقّق بخطوتين وجلساتك المفتوحة.' },
  ];
}

/** مسارات القائمة التي تشير إليها جولةُ كل دور — للفحص وحده */
export function roleTourPaths(): Array<[EffectiveRole, string]> {
  return Object.entries(ROLE_STEPS).flatMap(([r, steps]) => (steps ?? []).map((s) => [r as EffectiveRole, s.to] as [EffectiveRole, string]));
}
